import { Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { interval } from "rxjs";
import { take } from "rxjs/operators";
import { VoucherService } from "src/app/services/vouchers.service";
import { AddCardComponent } from "../add-card.component";

@Injectable()
export class CheckoutVoucherService {
  /** Blocks checkbox ionChange noise from unhiding vouchers while Flow remounts. */
  private suppressVoucherUiReset = false;

  constructor(private voucherService: VoucherService) {}

  /**
   * Fetch discount vouchers data and filter based on criteria
   */
  public fetchDiscountVouchersData(componentContext: AddCardComponent): void {
    this.voucherService
      .getVoucherList()
      .pipe(take(1))
      .subscribe({
        next: (res: Data[]) => {
          const currentTime = new Date().getTime();
          const filteredVouchers = res.filter((voucher) => {
            const voucherExpiryDate = voucher?.expiry_date?.toDate()?.getTime();
            return (
              voucher?.discount_voucher === true &&
              voucher?.isRedeemed === false &&
              voucher?.hide === false &&
              voucherExpiryDate > currentTime &&
              (voucher?.supplier?.toLowerCase() === "general" ||
                voucher?.supplier?.toLowerCase() === componentContext.purchaseDetail$?.value?.real_name?.toLowerCase())
            );
          }); // Filter for discount vouchers
          const hasVouchers = filteredVouchers.length > 1;
          componentContext.discountVouchersData = filteredVouchers.map((voucher, index) => ({
            ...voucher,
            isSelected: hasVouchers && index === 0,
            isDisabled: false,
            changeExpiryText: false,
            auto_discount: hasVouchers && index === 0,
          }));
          // Mirror try-again: apply discount for auto-selected voucher (UI tick alone does not set amount).
          if (hasVouchers) {
            const autoSelected = componentContext.discountVouchersData[0];
            this.onVoucherSelect({ detail: { checked: true } }, autoSelected, componentContext);
          }
        },
        error: (err) => {
          componentContext.discountVouchersData = [];
          return err;
        },
      });
    this.startDiscountVouchersExpiryCheck(componentContext);
  }

  /**
   * Start checking for expiring discount vouchers
   */
  public startDiscountVouchersExpiryCheck(componentContext: AddCardComponent): void {
    try {
      // To clear the existing expiry check logic
      componentContext.expiryCheckInterval?.unsubscribe();
      componentContext.expiryCheckInterval = interval(1000).subscribe(() => {
        const currentTime = new Date().getTime();
        componentContext.discountVouchersData = componentContext.discountVouchersData.filter((voucher) => {
          const voucherExpiryDate = voucher.expiry_date.toDate().getTime();
          const status = voucherExpiryDate > currentTime;
          const remainingTime = voucherExpiryDate - currentTime;
          if (remainingTime > 0 && remainingTime <= 60000) {
            // Voucher will expire in less than a minute
            this.updateExpiryTextInVoucher(componentContext, voucher);
          }
          if (!status) {
            this.resetDiscountAmountOnVoucherExpiry(componentContext, voucher);
          }
          return status;
        });
      });
    } catch (error) {
      return error;
    }
  }

  /**
   * Handle voucher selection
   */
  public onVoucherSelect(event: Data, selectedVoucher: Data, componentContext: AddCardComponent): void {
    componentContext.isChecked.set(event?.detail?.checked); // ✅ true or false
    if (selectedVoucher.isSelected) {
      componentContext.selectedDiscountVoucherData = { ...selectedVoucher };
      componentContext.discountVouchersData.forEach((voucher) => {
        voucher.isSelected = voucher === selectedVoucher;
        voucher.isDisabled = voucher !== selectedVoucher;
        if (voucher !== selectedVoucher) {
          voucher.isUsed = true; // Add 'used-voucher' class to others
        } else {
          voucher.isUsed = false; // Remove 'used-voucher' class from the selected one
        }
      });
      const previousDiscountValue = componentContext.selectedDiscountValue;
      this.voucherService
        .getVoucherLookupData(selectedVoucher.voucher_code)
        .pipe(take(1))
        .subscribe((data) => {
          if (data.length > 0) {
            const voucherData: Data = data[0];
            const voucherExpiryDate = voucherData.end_date.toDate().getTime();
            const now = new Date().getTime();
            if (voucherData.status && voucherExpiryDate > now) {
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, true);
              componentContext.selectedDiscountValue = selectedVoucher?.discount_percentage / 100;
              componentContext.purchaseDetail["voucher_discount"] =
                componentContext.selectedDiscountValue && componentContext.selectedDiscountValue > 0
                  ? componentContext.selectedDiscountValue
                  : 0;
              this.remountCheckoutFlowForDiscountChange(componentContext, previousDiscountValue);
            } else {
              this.voucherService.showInvalidVoucherToast();
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, true);
              componentContext.discountVouchersData = componentContext.discountVouchersData.filter(
                (voucher) => voucher.id !== selectedVoucher.id,
              );
              componentContext.discountVouchersData.forEach((voucher) => {
                voucher.isSelected = false;
                voucher.isDisabled = false;
                voucher.isUsed = false;
              });
              componentContext.selectedDiscountValue = 0;
              this.remountCheckoutFlowForDiscountChange(componentContext, previousDiscountValue);
            }
          }
        });
    } else {
      // Ignore sibling-checkbox / remount ionChange noise; only real uncheck of the applied voucher resets.
      const currentId = componentContext.selectedDiscountVoucherData?.id;
      if (this.suppressVoucherUiReset) {
        if (currentId && selectedVoucher?.id === currentId) {
          selectedVoucher.isSelected = true;
        }
        return;
      }
      if (currentId && selectedVoucher?.id && selectedVoucher.id !== currentId) {
        return;
      }
      this.resetVouchersUI(componentContext);
    }
  }

  /**
   * Reset discount voucher hide status
   */
  public resetDiscountVoucherHideStatus(componentContext: AddCardComponent): void {
    // If payment is not attempted then update discount voucher hide status on page refresh or back navigation
    if (!componentContext.addCardPaymentStatus) {
      this.voucherService.updateDiscountVoucherHideStatus(componentContext.selectedDiscountVoucherData?.id, false);
    }
  }

  /**
   * Reset discount amount when voucher expires
   */
  public resetDiscountAmountOnVoucherExpiry(componentContext: AddCardComponent, voucher: Data): void {
    try {
      const currentVoucherId = componentContext.selectedDiscountVoucherData?.id || "";
      if (voucher?.id === currentVoucherId) {
        this.resetVouchersUI(componentContext);
        this.voucherService.updateDiscountVoucherHideStatus(currentVoucherId, true);
      }
    } catch (error) {
      return error;
    }
  }

  /**
   * Reset all vouchers UI state
   */
  public resetVouchersUI(componentContext: AddCardComponent): void {
    try {
      const previousDiscountValue = componentContext.selectedDiscountValue;
      this.voucherService.updateDiscountVoucherHideStatus(componentContext.selectedDiscountVoucherData?.id, false);
      componentContext.selectedDiscountValue = 0;
      componentContext.selectedDiscountVoucherData = null;
      componentContext.discountVouchersData.forEach((voucher) => {
        voucher.isSelected = false;
        voucher.isDisabled = false;
        voucher.isUsed = false;
      });
      this.remountCheckoutFlowForDiscountChange(componentContext, previousDiscountValue);
    } catch (error) {
      return error;
    }
  }

  /**
   * Update expiry text for a voucher
   */
  public updateExpiryTextInVoucher(componentContext: AddCardComponent, expiredVoucher: Data): void {
    // Find the voucher in discountVouchersData by its ID
    const voucherIndex = componentContext.discountVouchersData.findIndex((voucher) => voucher.id === expiredVoucher.id);

    if (voucherIndex !== -1) {
      // Update the changeExpiryText node to true
      componentContext.discountVouchersData[voucherIndex].changeExpiryText = true;
    }
  }

  /**
   * Flow session amount is fixed at create time — remount so the charged amount matches the selected discount.
   * Suppress voucher UI reset during remount so ion-checkbox ionChange cannot clear hide / selection.
   */
  private remountCheckoutFlowForDiscountChange(
    componentContext: AddCardComponent,
    previousDiscountValue: number,
  ): void {
    if (
      componentContext.cardTokenizedCalled ||
      componentContext.addNewCardInProgress ||
      !componentContext.keys?.publicKey ||
      componentContext.selectedDiscountValue === previousDiscountValue
    ) {
      return;
    }
    const voucherId = componentContext.selectedDiscountVoucherData?.id;
    const shouldKeepHidden = !!voucherId && componentContext.selectedDiscountValue > 0;
    this.suppressVoucherUiReset = true;
    componentContext.suppressVoucherUiReset = true;
    void componentContext.initializeFrames();
    // Re-assert hide after remount CD settles; clear suppress on the next macrotask.
    setTimeout(() => {
      if (shouldKeepHidden) {
        this.voucherService.updateDiscountVoucherHideStatus(voucherId, true);
      }
      this.suppressVoucherUiReset = false;
      componentContext.suppressVoucherUiReset = false;
    }, 0);
  }
}
